import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DISPLAY_UI_LONG, RELATIVE } from 'src/constants/dates';
import { formatDateToString, getDateFormat } from 'src/utilis/utilis';
import { editShiftSelector, formSelector } from 'src/store/selectores/passengerShiftsSelectores';
import { useSelector } from 'react-redux';
import { IRootReducer } from 'src/store/reducers';
import moment, { Moment } from 'moment';
import DatePicker from 'src/components/DatePicker/DatePicker';
import { PlacemenContext } from '../../context';
import { StyledContainer } from './OrderDatesDetails.style';
import { RELATIVE_US } from '../../../../../../constants/dates';
import useDate from 'src/hooks/useDate';
import { DateDataForOrdering } from '../../types';
import helpers, { WORK_WEEK_DAYS } from './helpers.OrderDatesDetails';
import * as RangeStyles from './styles.OrderDatesRange';

interface Props {
    /** With both, the date is a picker; without them, the read-only summary as before */
    datesForOrders?: DateDataForOrdering[];
    onPickDates?: (dates: Moment[]) => void;
}

const Text = () => {
    const [placment] = useContext(PlacemenContext);
    const { t } = useTranslation();
    // const isShiftSelected = useSelector(
    //    (state: IRootReducer) => formSelector(state).isShiftSelected,
    // );
    const [detailsText, setDetailsText] = useState('');

    useEffect(() => {
        const { dates } = placment;
        if (
            !dates.length ||
            dates[0].relativeDate === 'Invalid date'
            // !isShiftSelected
        ) {
            setDetailsText('----.--.--');
        } else if (dates.length >= 2) {
            setDetailsText(`${t('pickedDays')}: ${dates.length}`);
        } else {
            setDetailsText(formatDateToString(dates[0].relativeDate, DISPLAY_UI_LONG, RELATIVE));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placment.dates, t]);
    return <div>{detailsText}</div>;
};

interface RangeProps {
    datesForOrders: DateDataForOrdering[];
    onPickDates: (dates: Moment[]) => void;
}

/**
 * "From – to" plus weekdays: fills the days grid below with every allowed day of the range on those
 * weekdays (days the grid blocks are skipped and counted). The grid still adds or removes single days.
 * Available once a shift is chosen, and only inside the grid's own range of days.
 */
const PlacementDateRange = ({ datesForOrders, onPickDates }: RangeProps) => {
    const { t } = useTranslation();
    const { shortsDays } = useDate();
    const isShiftSelected = !!useSelector((state: IRootReducer) => formSelector(state).isShiftSelected);
    const editedShift = useSelector((state: IRootReducer) => editShiftSelector(state));

    const [from, setFrom] = useState<Date | null>(null);
    const [to, setTo] = useState<Date | null>(null);
    const [weekdays, setWeekdays] = useState<number[]>(WORK_WEEK_DAYS);
    const [result, setResult] = useState<{ count: number; skipped: number } | null>(null);

    const gridRange = helpers.getDatesRange(datesForOrders);

    // Applies the range when the user changes it; a later change in the grid is the user's and stays
    const apply = (nextFrom: Date | null, nextTo: Date | null, nextWeekdays: number[]) => {
        if (!nextFrom || !nextTo) return;
        const { dates, skippedBlocked } = helpers.buildRangeDates(
            nextFrom,
            nextTo,
            nextWeekdays,
            datesForOrders,
            isShiftSelected,
            editedShift?.date
        );
        setResult({ count: dates.length, skipped: skippedBlocked });
        onPickDates(dates);
    };

    const onDateChange = (which: 'from' | 'to') => (date: Date | null) => {
        // Typing a partial date sends an invalid one; only a whole day inside the grid is taken
        const value = date && moment(date).isValid() ? date : null;
        if (which === 'from') {
            setFrom(value);
            if (!to && value) setTo(value);
            apply(value, to || value, weekdays);
        } else {
            setTo(value);
            apply(from, value, weekdays);
        }
    };

    const toggleWeekday = (day: number) => {
        const next = weekdays.includes(day) ? weekdays.filter((d) => d !== day) : [...weekdays, day].sort();
        setWeekdays(next);
        apply(from, to, next);
    };

    const pickerProps = {
        disabled: !isShiftSelected,
        // Blocked days can still be a range's edge; they are skipped inside it
        shouldDisableDate: (date: unknown) => !gridRange || !moment(date as Date).isBetween(gridRange.min, gridRange.max, 'day', '[]'),
        minDate: gridRange?.min.toDate(),
        maxDate: gridRange?.max.toDate(),
        format: 'dd.MM.yyyy',
        inputVariant: 'outlined' as const,
        size: 'small' as const,
        fullWidth: true,
        autoOk: true,
        invalidDateMessage: t('placementDatePickerInvalid'),
        minDateMessage: t('placementDatePickerInvalid'),
        maxDateMessage: t('placementDatePickerInvalid'),
    };

    const getStatusText = () => {
        if (!isShiftSelected) return t('placementDatePickerChooseShift');
        if (!result) return '';
        if (!result.count) return t('placementRangeNoDays');
        return `${t('placementRangeSummary', { count: result.count })}${
            result.skipped ? t('placementRangeSkipped', { count: result.skipped }) : ''
        }`;
    };

    return (
        <RangeStyles.Container>
            <RangeStyles.Fields>
                <DatePicker
                    {...pickerProps}
                    label={t('placementRangeFrom')}
                    value={from}
                    onChange={(date) => onDateChange('from')(date as Date | null)}
                />
                <DatePicker
                    {...pickerProps}
                    label={t('placementRangeTo')}
                    value={to}
                    onChange={(date) => onDateChange('to')(date as Date | null)}
                />
            </RangeStyles.Fields>

            <RangeStyles.WeekdaysRow role="group" aria-label={t('placementRangeDays')}>
                <span>{t('placementRangeDays')}</span>
                {[0, 1, 2, 3, 4, 5, 6].map((day) => {
                    const isOn = weekdays.includes(day);
                    return (
                        <RangeStyles.WeekdayButton
                            key={day}
                            type="button"
                            $pressed={isOn}
                            aria-pressed={isOn}
                            disabled={!isShiftSelected}
                            onClick={() => toggleWeekday(day)}
                        >
                            {shortsDays[day]}
                        </RangeStyles.WeekdayButton>
                    );
                })}
            </RangeStyles.WeekdaysRow>

            {getStatusText() && <RangeStyles.Status role="status">{getStatusText()}</RangeStyles.Status>}
        </RangeStyles.Container>
    );
};

const OrderDatesDetails = ({ datesForOrders, onPickDates }: Props) => {
    const [placment] = useContext(PlacemenContext);

    if (datesForOrders && onPickDates) {
        return <PlacementDateRange datesForOrders={datesForOrders} onPickDates={onPickDates} />;
    }

    return (
        <StyledContainer count={placment.dates.length}>
            <Text />
        </StyledContainer>
    );
};

export default OrderDatesDetails;
